import { paths } from '@app/routes/paths';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { useLogoutMutation } from '@features/auth/authApi';
import { useAuth } from '@features/auth/hooks/useAuth';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '@i18n/config';
import { persistLanguage } from '@i18n/languagePersistence';
import CheckIcon from '@mui/icons-material/Check';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LanguageIcon from '@mui/icons-material/Language';
import LightModeIcon from '@mui/icons-material/LightMode';
import LogoutIcon from '@mui/icons-material/Logout';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/PersonOutlineOutlined';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { UserAvatar } from '@shared/components/UserAvatar';
import { toggleColorMode } from '@theme/colorModeSlice';
import { tokens } from '@theme/tokens';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export interface UserMenuProps {
  /**
   * Trigger shape: `icon` = bare avatar button (legacy/header contexts),
   * `row` = full-width profile row for the rail footer (light text on the
   * dark rail), `avatarOnly` = centered avatar for the collapsed rail.
   * Row/avatarOnly open the menu upwards.
   */
  variant?: 'icon' | 'row' | 'avatarOnly';
}

/** Avatar treatment shared by all triggers — precision-hardware ring. */
const railAvatarSx = {
  width: 36,
  height: 36,
  background: tokens.gradient.navPill,
  color: '#ffffff',
  fontSize: 14,
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.25), 0 0 0 1px rgba(255,255,255,0.12)',
} as const;

export function UserMenu({ variant = 'icon' }: UserMenuProps) {
  const { user } = useAuth();
  const mode = useAppSelector((s) => s.colorMode.mode);
  const dispatch = useAppDispatch();
  const [logout, { isLoading: isLoggingOut }] = useLogoutMutation();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [langAnchor, setLangAnchor] = useState<HTMLElement | null>(null);

  const open = Boolean(anchorEl);
  const langOpen = Boolean(langAnchor);
  const isDark = mode === 'dark';
  const currentLang = (i18n.resolvedLanguage ?? i18n.language).split('-')[0] as SupportedLanguage;
  const currentLangLabel =
    SUPPORTED_LANGUAGES.find((l) => l.code === currentLang)?.nativeLabel ?? currentLang;

  const close = () => {
    setAnchorEl(null);
    setLangAnchor(null);
  };

  const handleChangeLanguage = async (code: SupportedLanguage) => {
    persistLanguage(code);
    await i18n.changeLanguage(code);
    close();
  };

  const handleProfile = async () => {
    close();
    await navigate(paths.profile);
  };

  const handleToggleTheme = () => {
    dispatch(toggleColorMode());
  };

  const handleLogout = async () => {
    close();
    await logout();
    await navigate(paths.login, { replace: true });
  };

  const openMenu = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };
  const triggerA11y = {
    'aria-label': t('nav.openAccountMenu'),
    'aria-haspopup': 'true' as const,
    'aria-expanded': open ? ('true' as const) : undefined,
  };

  // Row/avatarOnly sit at the bottom of the rail — the menu opens upwards.
  const opensUp = variant !== 'icon';

  return (
    <>
      {variant === 'row' && (
        <ButtonBase
          onClick={openMenu}
          {...triggerA11y}
          sx={{
            width: '100%',
            borderRadius: 3,
            p: 1,
            gap: 1.25,
            justifyContent: 'flex-start',
            textAlign: 'left',
            color: '#e8efee',
            transition: 'background-color 180ms ease-out',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
          }}
        >
          <UserAvatar user={user} sx={railAvatarSx} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" noWrap sx={{ color: 'inherit' }}>
              {user?.name ?? t('nav.account')}
            </Typography>
            {user?.email && (
              <Typography
                variant="caption"
                noWrap
                sx={{ display: 'block', color: 'rgba(232,239,238,0.65)' }}
              >
                {user.email}
              </Typography>
            )}
          </Box>
          <MoreVertIcon sx={{ fontSize: 18, color: 'rgba(232,239,238,0.65)' }} />
        </ButtonBase>
      )}
      {variant === 'avatarOnly' && (
        <Tooltip title={user?.name ?? t('nav.account')} placement="right">
          <IconButton onClick={openMenu} {...triggerA11y} sx={{ p: 0.5 }}>
            <UserAvatar user={user} sx={railAvatarSx} />
          </IconButton>
        </Tooltip>
      )}
      {variant === 'icon' && (
        <Tooltip title={user?.name ?? t('nav.account')}>
          <IconButton onClick={openMenu} {...triggerA11y} sx={{ p: 0.5 }}>
            <UserAvatar user={user} sx={railAvatarSx} />
          </IconButton>
        </Tooltip>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        anchorOrigin={
          opensUp
            ? { vertical: 'top', horizontal: 'left' }
            : { vertical: 'bottom', horizontal: 'right' }
        }
        transformOrigin={
          opensUp
            ? { vertical: 'bottom', horizontal: 'left' }
            : { vertical: 'top', horizontal: 'right' }
        }
        slotProps={{
          paper: { sx: { minWidth: 240 } },
        }}
      >
        {user && (
          <Box sx={{ px: 2, py: 1.25 }}>
            <Typography variant="subtitle2" noWrap>
              {user.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {user.email}
            </Typography>
          </Box>
        )}
        <Divider />

        <MenuItem
          onClick={() => {
            void handleProfile();
          }}
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('nav.profile')}</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleToggleTheme}>
          <ListItemIcon>
            {isDark ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{t('nav.darkMode')}</ListItemText>
          <Switch
            edge="end"
            checked={isDark}
            size="small"
            tabIndex={-1}
            disableRipple
            slotProps={{ input: { 'aria-label': t('nav.darkMode'), tabIndex: -1 } }}
            sx={{ pointerEvents: 'none' }}
          />
        </MenuItem>

        <MenuItem
          onClick={(e) => {
            setLangAnchor(e.currentTarget);
          }}
          aria-haspopup="true"
          aria-expanded={langOpen ? 'true' : undefined}
        >
          <ListItemIcon>
            <LanguageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('common.language')}</ListItemText>
          <Typography variant="caption" color="text.secondary">
            {currentLangLabel}
          </Typography>
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={() => {
            void handleLogout();
          }}
          disabled={isLoggingOut}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('auth.signOut')}</ListItemText>
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={langAnchor}
        open={langOpen}
        onClose={() => {
          setLangAnchor(null);
        }}
        // Beside the account menu, never over it: to the right of the rail, to the left of a header.
        anchorOrigin={{ vertical: 'center', horizontal: opensUp ? 'right' : 'left' }}
        transformOrigin={{ vertical: 'center', horizontal: opensUp ? 'left' : 'right' }}
        slotProps={{
          // The items sit 6 px inside their menu: this clears the account menu by a small gap.
          paper: { sx: { minWidth: 180, ml: opensUp ? 1.25 : -1.25 } },
        }}
      >
        {SUPPORTED_LANGUAGES.map((lang) => (
          <MenuItem
            key={lang.code}
            selected={lang.code === currentLang}
            onClick={() => {
              void handleChangeLanguage(lang.code);
            }}
          >
            <ListItemIcon>
              {lang.code === currentLang ? (
                <CheckIcon fontSize="small" />
              ) : (
                <Box sx={{ width: 20 }} />
              )}
            </ListItemIcon>
            <ListItemText primary={lang.nativeLabel} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
